import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Swords, Trophy, Crown, Flame, Link2, CalendarClock, Users, Shield } from "lucide-react";
import { api } from "../lib/api";
import { Page, PageTitle, Loader, Empty, Card } from "../components/UI";

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return null; }
}

function expectedText(iso) {
  if (!iso) return "scheduling automatically";
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  if (diff <= 20) return "expected imminently";
  if (diff < 90) return `expected in ~${Math.round(diff)}s`;
  if (diff < 3600) return `expected in ~${Math.round(diff / 60)} min`;
  return `expected ${fmtDate(iso)}`;
}

export default function RivalryPage() {
  const { type, pair } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setD(null); setErr(null);
    api.get(`/rivalry/${type}/${pair}`).then(({ data }) => setD(data))
      .catch((e) => setErr(e?.response?.status === 404 ? "notfound" : "error"));
  }, [type, pair]);

  if (err === "notfound") return <Page><Empty label="One or both teams could not be found." /></Page>;
  if (err) return <Page><Empty label="Could not load this rivalry." /></Page>;
  if (!d) return <Loader label="Loading rivalry…" />;

  const { a, b, head_to_head: hh, matches, records, top_performers, iconic_duels, trophies, next_chapter } = d;
  const played = hh.played;

  return (
    <Page>
      <PageTitle eyebrow={`Head-to-Head · ${type === "nations" ? "National Teams" : "Clubs"}`}
        title={`${a.name} vs ${b.name}`}
        subtitle="Every number derived from official match and duel data. Exhibitions are shown but excluded from the official record." />

      {/* Scoreline hero */}
      <div data-testid="rivalry-h2h" className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-6 sm:p-8 mb-8 relative overflow-hidden">
        <div className="absolute inset-0 mesh-grid opacity-20 pointer-events-none" />
        <div className="relative">
          <div className="grid grid-cols-3 items-center gap-4">
            <SideHead ref_={a} />
            <div className="text-center">
              <div data-testid="rivalry-record" className="font-heading text-5xl sm:text-6xl font-black tabular-nums text-slate-50 leading-none">
                <span className="text-[#E5B842]">{hh.a.w}</span>
                <span className="text-slate-600 mx-2">–</span>
                <span className="text-slate-400">{hh.a.d}</span>
                <span className="text-slate-600 mx-2">–</span>
                <span className="text-[#F0C72A]">{hh.a.l}</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mt-2">W · D · L ({a.name})</div>
              <div className="text-sm text-slate-400 mt-3">{played} official meeting{played === 1 ? "" : "s"}</div>
            </div>
            <SideHead ref_={b} right />
          </div>
          {hh.streak && (
            <div data-testid="rivalry-streak" className="mt-6 flex items-center justify-center gap-2 text-sm">
              <Flame className="w-4 h-4 text-[#E5B842]" />
              <span className="font-heading font-bold uppercase tracking-wide text-slate-100">{hh.streak.text}</span>
            </div>
          )}
          <div className="mt-6 grid grid-cols-2 gap-4 max-w-md mx-auto text-center">
            <PointsCol ref_={a} rec={hh.a} />
            <PointsCol ref_={b} rec={hh.b} />
          </div>
        </div>
      </div>

      {played === 0 && matches.length === 0 ? (
        <div data-testid="rivalry-empty"><Empty label={`${a.name} and ${b.name} have never met.`} /></div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* The story so far */}
            <section data-testid="rivalry-matches">
              <H icon={<Swords className="w-5 h-5 text-[#E5B842]" />}>The Story So Far</H>
              <Card className="overflow-hidden">
                {matches.length === 0 ? <div className="p-5 text-slate-500 text-sm">No matches yet.</div> :
                  matches.map((m) => (
                    <Link key={m.match_id} to={m.link} data-testid={`rivalry-match-${m.match_id}`}
                      className="flex items-center gap-3 px-4 py-3 border-t border-[#2b2512]/60 first:border-t-0 hover:bg-[#161616]/60"
                      style={{ transition: "background-color 0.15s ease" }}>
                      <Badge label={m.badge} />
                      <span className="font-mono text-[10px] text-slate-500 w-20 shrink-0">{fmtDate(m.date) || "—"}</span>
                      <span className="flex-1 text-sm text-slate-200 truncate">
                        {m.status === "FINAL" && m.home_points != null ? (
                          <span className="font-heading font-black tabular-nums">
                            {m.home_points}<span className="text-slate-600">–</span>{m.away_points}
                          </span>
                        ) : <span className="font-mono text-[10px] uppercase text-slate-500">{(m.status || "").replace(/_/g, " ")}</span>}
                        <span className="text-slate-500 ml-2 font-mono text-[10px]">{m.public_id}</span>
                      </span>
                    </Link>
                  ))}
              </Card>
            </section>

            {/* Top performers */}
            <section data-testid="rivalry-performers">
              <H icon={<Users className="w-5 h-5 text-[#E5B842]" />}>Top Performers</H>
              <div className="grid sm:grid-cols-2 gap-4">
                <PerformerCol ref_={a} items={top_performers.a} testid="performers-a" />
                <PerformerCol ref_={b} items={top_performers.b} testid="performers-b" />
              </div>
            </section>

            {/* Iconic duels */}
            {iconic_duels && iconic_duels.length > 0 && (
              <section data-testid="rivalry-iconic">
                <H icon={<Swords className="w-5 h-5 text-[#E5B842]" />}>Iconic Duels</H>
                <Card className="divide-y divide-[#2b2512]/60">
                  {iconic_duels.map((i, idx) => (
                    <div key={idx} data-testid={`iconic-${idx}`} className="flex items-center gap-3 px-4 py-3 text-sm">
                      <PLink p={i.player_a} /><span className="font-heading font-black text-[#E5B842] tabular-nums">{i.wins_a}</span>
                      <span className="text-slate-600 text-xs">vs</span>
                      <span className="font-heading font-black text-[#F0C72A] tabular-nums">{i.wins_b}</span><PLink p={i.player_b} />
                      <span className="ml-auto font-mono text-[10px] uppercase text-slate-500">{i.count} duels</span>
                    </div>
                  ))}
                </Card>
              </section>
            )}
          </div>

          <div className="space-y-8">
            {/* Next chapter */}
            <section data-testid="rivalry-next">
              <H icon={<CalendarClock className="w-5 h-5 text-[#E5B842]" />}>Next Chapter</H>
              {next_chapter?.series ? (
                <div data-testid="rivalry-next-series" className="rounded-2xl foil-border bg-gradient-to-br from-[#2a2410] to-[#070707] p-5">
                  <Badge label={next_chapter.badge} />
                  <div className="mt-2 font-heading text-lg font-black uppercase text-slate-50">{a.name} vs {b.name}</div>
                  <div className="text-xs text-slate-400 font-mono mt-1">{next_chapter.competition_name} · perpetual series</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#E5B842] mt-2">
                    Next friendly {expectedText(next_chapter.expected_at)}
                  </div>
                </div>
              ) : next_chapter ? (
                <Link to={next_chapter.link} className="block rounded-2xl foil-border bg-gradient-to-br from-[#2a2410] to-[#070707] p-5 hover:border-[#E5B842]/60" style={{ transition: "border-color 0.15s ease" }}>
                  <Badge label={next_chapter.badge} />
                  <div className="mt-2 font-heading text-lg font-black uppercase text-slate-50">{a.name} vs {b.name}</div>
                  <div className="text-xs text-slate-400 font-mono mt-1">{next_chapter.competition_name}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#E5B842] mt-2">{(next_chapter.status || "").replace(/_/g, " ")} · {next_chapter.public_id}</div>
                </Link>
              ) : <Card className="p-5 text-sm text-slate-500" data-testid="rivalry-no-next">No meeting scheduled.</Card>}
            </section>

            {/* Rivalry records */}
            <section data-testid="rivalry-records">
              <H icon={<Flame className="w-5 h-5 text-[#E5B842]" />}>Rivalry Records</H>
              <div className="space-y-3">
                <RecRow label="Biggest win" value={records.biggest_win ? `+${records.biggest_win.margin} (${records.biggest_win.score})` : "—"}
                  sub={records.biggest_win ? `${records.biggest_win.winner?.name} · ${fmtDate(records.biggest_win.date)}` : null}
                  link={records.biggest_win?.link} testid="rec-biggest-win" />
                <RecRow label="Highest-scoring" value={records.highest_scoring ? `${records.highest_scoring.total} pts (${records.highest_scoring.score})` : "—"}
                  sub={records.highest_scoring ? fmtDate(records.highest_scoring.date) : null} link={records.highest_scoring?.link} testid="rec-highest-scoring" />
                <RecRow label="Highest winning coupon" value={records.highest_coupon ? records.highest_coupon.value_str : "—"}
                  sub={records.highest_coupon?.player?.name} subLink={records.highest_coupon?.player?.link} link={records.highest_coupon?.link} testid="rec-highest-coupon" />
                <RecRow label="Most duels won" value={records.most_duels_player ? `${records.most_duels_player.wins}` : "—"}
                  sub={records.most_duels_player?.player?.name} subLink={records.most_duels_player?.player?.link} testid="rec-most-duels" />
              </div>
            </section>

            {/* Trophy comparison */}
            <section data-testid="rivalry-trophies">
              <H icon={<Trophy className="w-5 h-5 text-[#E5B842]" />}>Trophy Comparison</H>
              <Card className="p-4">
                {trophies.a.map((t, i) => (
                  <div key={i} className="flex items-center justify-between py-2 text-sm border-b border-[#2b2512]/50 last:border-0">
                    <span className="font-heading font-black text-[#E5B842] tabular-nums w-10 text-left">{t.count}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex-1 text-center">{t.label}</span>
                    <span className="font-heading font-black text-[#F0C72A] tabular-nums w-10 text-right">{trophies.b[i]?.count ?? 0}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                  <span>{a.name}</span><span>{b.name}</span>
                </div>
              </Card>
            </section>
          </div>
        </div>
      )}
    </Page>
  );
}

function SideHead({ ref_, right }) {
  return (
    <Link to={ref_.link} className={`flex flex-col items-center gap-2 ${right ? "sm:items-center" : ""}`}>
      <div className="w-16 h-16 rounded-full flex items-center justify-center font-heading text-2xl font-black text-[#000000] ring-2 ring-[#E5B842]/50" style={{ background: "linear-gradient(135deg,#F3D279,#C99E30)" }}>
        {ref_.name?.[0]?.toUpperCase()}
      </div>
      <div className="font-heading font-black uppercase text-slate-50 text-center text-sm truncate max-w-[9rem]">{ref_.name}</div>
    </Link>
  );
}

function PointsCol({ ref_, rec }) {
  return (
    <div className="rounded-lg bg-[#000000] border border-[#2b2512] p-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 truncate">{ref_.name}</div>
      <div className="font-heading text-xl font-black text-slate-100 tabular-nums">{rec.pf} <span className="text-[10px] text-slate-500">TP for</span></div>
      <div className="font-mono text-[11px] text-slate-500">{rec.pa} against</div>
    </div>
  );
}

function PerformerCol({ ref_, items, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#E5B842] mb-3 truncate flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> {ref_.name}</div>
      {(!items || items.length === 0) ? <div className="text-slate-500 text-sm">No data yet.</div> :
        items.map((p, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 text-sm">
            <PLink p={p.player} />
            <span className="font-mono text-slate-400 text-xs">{p.team_points} TP · {p.wins}W</span>
          </div>
        ))}
    </Card>
  );
}

function PLink({ p }) {
  if (!p) return <span className="text-slate-500">—</span>;
  return p.link ? (
    <Link to={p.link} className="text-slate-100 hover:text-[#E5B842] truncate" style={{ transition: "color 0.15s ease" }}>{p.name}</Link>
  ) : <span className="text-slate-200 truncate">{p.name}</span>;
}

function RecRow({ label, value, sub, subLink, link, testid }) {
  const head = (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</span>
      <span className="font-heading text-xl font-black text-[#E5B842] tabular-nums">{value}</span>
    </div>
  );
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-3 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }} data-testid={testid}>
      {link ? <Link to={link} className="block">{head}</Link> : head}
      {sub && (
        <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
          {link && <Link2 className="w-3 h-3" />}
          {subLink ? <Link to={subLink} className="hover:text-[#E5B842]">{sub}</Link> : sub}
        </div>
      )}
    </div>
  );
}

function Badge({ label }) {
  const tone = {
    League: "text-emerald-300 border-emerald-500/30", Cup: "text-amber-300 border-amber-500/30",
    Continental: "text-[#F0C72A] border-[#E5B31E]/30", Championship: "text-[#E5B842] border-[#E5B842]/40",
    "Nations Series": "text-fuchsia-300 border-fuchsia-500/30", Exhibition: "text-slate-400 border-slate-600/40",
  }[label] || "text-slate-300 border-[#2b2512]";
  return <span className={`shrink-0 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${tone}`}>{label}</span>;
}

function H({ icon, children }) {
  return <h2 className="flex items-center gap-2 font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3">{icon}{children}</h2>;
}
